import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { publicConfig } from '../config/env';
import { useSession } from '../auth/SessionProvider';
import { callbackUrl, safeReturnTo } from '../auth/session';
import { CHECK_YOUR_EMAIL, isEmailShaped, normaliseEmail } from '../auth/authErrors';
import { AuthLayout, AuthLink, Field, FormNotice } from './AuthLayout';
import { EndLinkSession } from '../auth/RequireAuth';

/**
 * Where an emailed link lands.
 *
 * The client picks the tokens out of the URL itself; this screen waits for that
 * to settle, cleans the address bar so a verification token is not left sitting
 * in history, and then sends the person on. A link that failed says so — an
 * expired link is the ordinary case, not an error worth a stack trace.
 */
export function AuthCallback() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { state, loading, refresh, openedByLink } = useSession();
    const [failed, setFailed] = useState<string | null>(null);
    // Set once this screen has ended the link's session, so the sign-in page can say the address is confirmed.
    const endedLink = useRef(false);

    const errorInLink = params.get('error_description') ?? params.get('error');
    const returnTo = safeReturnTo(params.get('returnTo'), '/');

    useEffect(() => {
        if (errorInLink) {
            setFailed(
                /expired|invalid/i.test(errorInLink)
                    ? 'That link has expired or has already been used.'
                    : 'That link could not be used.',
            );
            return;
        }
        void refresh().then(() => {
            // The tokens arrive in the fragment. Once the client has consumed
            // them there is no reason to keep them in the address bar.
            if (typeof window !== 'undefined' && window.location.hash) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
        });
    }, [errorInLink, refresh]);

    if (failed) {
        return (
            <AuthLayout
                title="That link is no longer valid"
                lede="Verification links are good for one hour and can be used once. If more than one email arrived, only the newest link works."
                footer={<AuthLink to="/login">Back to sign in</AuthLink>}
            >
                <Button variant="primary" size="lg" className="w-full" onClick={() => navigate('/verify-email')}>
                    Send a new link
                </Button>
            </AuthLayout>
        );
    }

    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Signing you in</span>
                <Spinner className="text-accent-text w-6 h-6" />
            </div>
        );
    }

    // The link proved the address and opened a session. That session could set a new password without the
    // current one (Auth's rule for emailed links), so it ends here: the person signs in with the password
    // they chose, and carries on where the link was taking them (docs/product/phase-0/03 §2).
    if (state === 'active' && openedByLink) {
        endedLink.current = true;
        return <EndLinkSession />;
    }
    if (state === 'active') return <Navigate to={returnTo} replace />;
    if (endedLink.current) return <Navigate to={`/login?confirmed=1&returnTo=${encodeURIComponent(returnTo)}`} replace />;
    return <Navigate to="/login" replace />;
}

/**
 * Signed in, but the address has not been proved yet.
 *
 * The database refuses to create a company or accept an invitation for an
 * unverified account, so this screen exists to explain that before somebody
 * runs into it, and to send another link.
 */
export function VerifyEmail() {
    const { email, verified, signOut, refresh } = useSession();
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [address, setAddress] = useState(email ?? '');

    useEffect(() => { void refresh(); }, [refresh]);

    if (verified) return <Navigate to="/" replace />;

    const resend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);
        if (!isEmailShaped(address)) {
            setSent(true);
            return;
        }
        setBusy(true);
        const { error: authError } = await supabase().auth.resend({
            type: 'signup',
            email: normaliseEmail(address),
            options: { emailRedirectTo: callbackUrl(publicConfig().appUrl, '/auth/callback') },
        });
        setBusy(false);
        if (authError && /rate limit|too many/i.test(authError.message ?? '')) {
            setError('Too many attempts. Wait a few minutes and try again.');
            return;
        }
        setSent(true);
    };

    return (
        <AuthLayout
            title="Confirm your email address"
            lede={email ? `We sent a link to ${email}. Open it and you are in.` : 'Enter the address you signed up with, and we send a new link to confirm it.'}
            footer={email ? (
                <button type="button" onClick={() => { void signOut(); }} className="text-accent-text font-semibold hover:underline">
                    Sign out
                </button>
            ) : (
                <Link to="/login" className="text-accent-text font-semibold hover:underline">Back to sign in</Link>
            )}
        >
            {sent && <FormNotice tone="ok">{CHECK_YOUR_EMAIL}</FormNotice>}
            {error && <FormNotice tone="error">{error}</FormNotice>}

            <p className="text-body text-label-2 leading-relaxed mb-5">
                Nothing arrived? Check the spam folder, or send it again. You can correct the
                address here if it is wrong.
            </p>

            <form onSubmit={resend} noValidate>
                <Field large
                    label="Email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                />
                <Button type="submit" variant="secondary" size="lg" className="w-full" busy={busy}>
                    {busy ? 'Sending…' : 'Send the link again'}
                </Button>
            </form>
        </AuthLayout>
    );
}
